import { useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";

import { cn } from "@/utils";
import { Drawer } from "@/components";
import { useResponsiveness, useTranslate } from "@/providers/context";
import { DRAWER_DESCRIPTION } from "@/const";

interface DescriptionProps {
  className?: string;
  desc: string;
  autoExpand?: boolean;
}

export const Description = ({
  desc,
  className,
  autoExpand,
}: DescriptionProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const { openDrawer, setOpenDrawer } = useResponsiveness();
  const { translate } = useTranslate();

  const [isOverflowing, setIsOverflowing] = useState(false);

  const purifiedDesc = useMemo(() => {
    if (desc) {
      return DOMPurify.sanitize(desc);
    }
    return "";
  }, [desc]);

  /**
   * SeeMore is triggered if content exceeds 2 lines (42px) at default font size
   */

  useEffect(() => {
    if (!autoExpand) {
      const checkContentHeight = () => {
        const currentHeight = contentRef.current?.clientHeight ?? 0;
        // Check if the content height exceeds 42px
        setIsOverflowing(currentHeight > 42);
      };

      checkContentHeight();

      // Optional: re-check on window resize if your design is responsive
      window.addEventListener("resize", checkContentHeight);
      return () => window.removeEventListener("resize", checkContentHeight);
    }
  }, [purifiedDesc, autoExpand]); // Re-check when purifiedDesc changes

  return (
    <>
      <div className={cn(["", className])}>
        <div
          className={cn([
            "p2-regular overflow-clip [&>p>span]:!font-sans",
            !autoExpand && "max-h-[43px]",
          ])}
          ref={contentRef}
          dangerouslySetInnerHTML={{ __html: purifiedDesc }}
        />
        {isOverflowing && (
          <div
            className="p2-semibold text-item-primary active:text-item-primary cursor-pointer pt-2 capitalize hover:text-item-secondary"
            onClick={() => setOpenDrawer(DRAWER_DESCRIPTION)}
          >
            {translate("See full description")}
          </div>
        )}
      </div>
      <Drawer
        title={translate("Description")}
        open={openDrawer === DRAWER_DESCRIPTION}
        onOpenChange={(open) => setOpenDrawer(open ? DRAWER_DESCRIPTION : "")}
      >
        <div
          className="p2-regular [&>p>span]:!font-sans"
          dangerouslySetInnerHTML={{ __html: purifiedDesc }}
        />
      </Drawer>
    </>
  );
};
